/**
 * DensityProvider — workspace density preference (Comfortable / Compact).
 *
 * Persists to localStorage and exposes a `data-density` attribute on
 * <html> so Tailwind utilities can target it via group/parent selectors:
 *
 *   className="px-5 py-3 [html[data-density=compact]_&]:px-3 [html[data-density=compact]_&]:py-1.5"
 *
 * The simpler path is to read `useDensity()` and pick className conditionally.
 * Initial release uses the data-attribute approach so it can be styled
 * declaratively from CSS in future without re-rendering.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type Density = 'comfortable' | 'compact';

interface DensityContextValue {
  density: Density;
  setDensity: (next: Density) => void;
  toggle: () => void;
}

const DENSITY_STORAGE_KEY = 'aicg-density';
const DensityContext = createContext<DensityContextValue | null>(null);

function readInitial(): Density {
  if (typeof window === 'undefined') return 'comfortable';
  const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
  return stored === 'compact' ? 'compact' : 'comfortable';
}

export function DensityProvider({ children }: { children: React.ReactNode }) {
  const [density, setDensityState] = useState<Density>(readInitial);

  // Reflect onto <html> so CSS / Tailwind data-* selectors can target it.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.density = density;
  }, [density]);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
    try {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      /* private mode: ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    setDensity(density === 'compact' ? 'comfortable' : 'compact');
  }, [density, setDensity]);

  const value = useMemo<DensityContextValue>(
    () => ({ density, setDensity, toggle }),
    [density, setDensity, toggle],
  );

  return (
    <DensityContext.Provider value={value}>{children}</DensityContext.Provider>
  );
}

export function useDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) {
    // Fall back to a no-op context so consumers don't crash if used outside a
    // provider (e.g. in tests).
    return {
      density: 'comfortable',
      setDensity: () => {},
      toggle: () => {},
    };
  }
  return ctx;
}
